import { isAxiosError } from 'axios'
import i18n from '@/i18n'
import type { ErrorCode, ErrorResponse } from '@/api/model'

/**
 * Central API error handling (replacement for the old `requestNotifier.tsx` from the
 * CustomerPortal). Breaks a raw Axios error down into the backend `ErrorResponse`
 * and translates the `errorCode` into a user-readable message.
 *
 * Usually not called directly, but via `notify.apiError(error, text)` —
 * explicitly in each mutation's `onError`/`catch` (deliberately NO global handler:
 * nothing written = no toast). Uses the standalone `i18n` instance, so it also works
 * outside of React components.
 */
export interface ParsedApiError {
  errorCode: ErrorCode | null
  /** Raw backend text (English, developer-oriented) — usually not shown to the user. */
  backendMessage: string | null
  traceId: string | null
  statusCode: number | null
  /** Extra payload of some errors (e.g. validation result on Excel import). */
  data: unknown
}

function isErrorResponse(value: unknown): value is ErrorResponse {
  return typeof value === 'object' && value !== null && 'errorCode' in value
}

/** Extracts the backend `ErrorResponse` from an arbitrary error (where present). */
export function parseApiError(error: unknown): ParsedApiError {
  if (isAxiosError(error)) {
    const body = error.response?.data
    const headerTraceId = error.response?.headers?.['x-trace-id']
    const fallbackTraceId = typeof headerTraceId === 'string' ? headerTraceId : null

    if (isErrorResponse(body)) {
      return {
        errorCode: body.errorCode ?? null,
        backendMessage: body.message ?? null,
        traceId: body.traceId ?? fallbackTraceId,
        statusCode: body.statusCode ?? error.response?.status ?? null,
        data: body.data ?? null,
      }
    }

    return {
      errorCode: null,
      backendMessage: null,
      traceId: fallbackTraceId,
      statusCode: error.response?.status ?? null,
      data: body ?? null,
    }
  }

  return { errorCode: null, backendMessage: null, traceId: null, statusCode: null, data: null }
}

/**
 * True, wenn kein erreichbares Backend geantwortet hat: entweder gar keine HTTP-Antwort
 * (Browser-Verbindungsfehler) oder ein Gateway-Fehler 502/503/504 (Backend down hinter dem
 * Reverse-/Vite-Proxy). Genutzt an Auth-Call-Sites, um „Server nicht erreichbar" von einer
 * echten fachlichen Antwort (falsche Zugangsdaten, ungültiger Link) zu unterscheiden.
 */
export function isServerUnreachable(error: unknown): boolean {
  const { statusCode } = parseApiError(error)
  return statusCode === null || statusCode === 502 || statusCode === 503 || statusCode === 504
}

/** Translation for the specific `errorCode` (`errors:codes.<Code>`), or null when there's no catalog entry. */
export function translateApiErrorCode(errorCode: ErrorCode | null): string | null {
  if (!errorCode) return null
  const key = `codes.${errorCode}`
  return i18n.exists(key, { ns: 'errors' }) ? i18n.t(key, { ns: 'errors' }) : null
}

/**
 * User-readable message for a parsed error.
 * Order: translation for the specific `errorCode` (`errors:codes.<Code>`) →
 * page-specific `fallback` (e.g. `meta.errorMessage`) → generic message.
 */
export function resolveApiErrorMessage(parsed: ParsedApiError, fallback?: string): string {
  return translateApiErrorCode(parsed.errorCode) ?? fallback ?? i18n.t('generic', { ns: 'errors' })
}
